/**
 * Canonical Terms and Conditions & Privacy Policy copy, mirrored verbatim from
 * nulanotes.com/terms and nulanotes.com/privacy (effective July 1, 2026).
 *
 * Keep in sync with the marketing site (apps/www/src/pages/Terms.tsx and
 * Privacy.tsx). Rendered by <LegalModal />.
 */

interface LegalDocContent {
  title: string;
  url: string;
  updated: string;
  intro: React.ReactNode;
  sections: { heading: string; body: React.ReactNode }[];
}

const legalEmail = <a href="mailto:legal@nulanotes.com">legal@nulanotes.com</a>;

export const TERMS: LegalDocContent = {
  title: 'Terms and Conditions',
  url: 'https://nulanotes.com/terms',
  updated: 'Effective July 1, 2026',
  intro: (
    <>
      <p>These Terms and Conditions (&ldquo;Terms&rdquo;) govern your access to and use of Nula&rsquo;s websites, applications, AI features, integrations, and related services (collectively, the &ldquo;Service&rdquo;).</p>
      <p>By accessing or using Nula, you agree to these Terms. If you do not agree, you may not use the Service.</p>
    </>
  ),
  sections: [
    {
      heading: '1. About Nula',
      body: (
        <>
          <p>Nula is an AI-powered workspace that helps users capture conversations, generate notes and visual artifacts, organize information into initiatives, identify decisions and action items, and coordinate work through AI-assisted workflows and third-party integrations.</p>
          <p>Based on information provided by you or connected services, Nula may generate outputs such as:</p>
          <ul>
            <li>Transcripts and summaries.</li>
            <li>Mind maps, diagrams, and charts.</li>
            <li>Presentations and documents.</li>
            <li>Workflows and tasks.</li>
            <li>Recommendations and other outputs.</li>
          </ul>
        </>
      ),
    },
    {
      heading: '2. Eligibility and Accounts',
      body: (
        <>
          <p>You must be at least 18 years old, or the legal age required to enter into a binding agreement in your jurisdiction, to use Nula.</p>
          <p>You agree to:</p>
          <ul>
            <li>Provide accurate account information.</li>
            <li>Keep your login credentials secure.</li>
            <li>Promptly notify us of unauthorized account access.</li>
            <li>Accept responsibility for activity conducted through your account.</li>
          </ul>
          <p>You may not share account credentials except as expressly permitted under your subscription plan.</p>
        </>
      ),
    },
    {
      heading: '3. Organizations and Administrators',
      body: (
        <>
          <p>If you use Nula through an employer, company, team, or other organization, that organization may control your account and associated workspace.</p>
          <p>Organization administrators may be able to:</p>
          <ul>
            <li>Add or remove users.</li>
            <li>Access, manage, export, or delete workspace content.</li>
            <li>Configure integrations and permissions.</li>
            <li>Control retention and security settings.</li>
            <li>Suspend or terminate workspace access.</li>
          </ul>
          <p>If you create an account using an organization-controlled email address, the account may become associated with that organization.</p>
        </>
      ),
    },
    {
      heading: '4. Meeting Recording and Consent',
      body: (
        <>
          <p>Nula may record, transcribe, summarize, or analyze meetings and conversations when enabled by a user.</p>
          <p>You are responsible for:</p>
          <ul>
            <li>Informing all participants that Nula is being used.</li>
            <li>Obtaining all notices, permissions, and consents required by applicable law.</li>
            <li>Complying with recording, wiretapping, employment, confidentiality, and privacy laws.</li>
            <li>Ensuring you are authorized to provide meeting content to Nula.</li>
          </ul>
          <p>You may not use Nula to secretly or unlawfully record, monitor, or analyze another person.</p>
          <p>Nula does not determine whether consent is legally required in a particular jurisdiction. You are solely responsible for making that determination.</p>
        </>
      ),
    },
    {
      heading: '5. User Content',
      body: (
        <>
          <p>&ldquo;User Content&rdquo; means information you or your authorized integrations submit to Nula, including:</p>
          <ul>
            <li>Audio and video recordings.</li>
            <li>Meeting transcripts and notes.</li>
            <li>Messages and conversations.</li>
            <li>Documents and uploaded files.</li>
            <li>Initiative data.</li>
            <li>Tasks, decisions, comments, and instructions.</li>
            <li>Content imported from third-party services.</li>
            <li>Visual artifacts and other materials you create using the Service.</li>
          </ul>
          <p>You retain ownership of your User Content.</p>
          <p>You grant Nula a limited, worldwide, non-exclusive license to host, copy, process, transmit, display, and use User Content only as necessary to:</p>
          <ul>
            <li>Provide and maintain the Service.</li>
            <li>Generate requested AI outputs.</li>
            <li>Operate integrations and workflows.</li>
            <li>Prevent fraud, abuse, and security incidents.</li>
            <li>Comply with applicable law.</li>
            <li>Improve the Service as permitted by our Privacy Policy and your account settings.</li>
          </ul>
          <p>You represent that you have all rights and permissions necessary to provide User Content to Nula.</p>
        </>
      ),
    },
    {
      heading: '6. AI-Generated Outputs',
      body: (
        <>
          <p>Nula uses artificial intelligence to generate summaries, visual artifacts, recommendations, tasks, classifications, and other outputs.</p>
          <p>AI-generated outputs may be incomplete, inaccurate, outdated, misleading, or unsuitable for your intended purpose.</p>
          <p>You are responsible for reviewing and verifying outputs before relying on them or sharing them with others.</p>
          <p>Nula does not provide legal, medical, financial, employment, compliance, or other professional advice. You should obtain advice from a qualified professional where appropriate.</p>
          <p>You retain any rights you may have in outputs generated specifically for you, subject to:</p>
          <ul>
            <li>Applicable law.</li>
            <li>Third-party rights.</li>
            <li>These Terms.</li>
            <li>The possibility that similar outputs may be generated for other users.</li>
          </ul>
        </>
      ),
    },
    {
      heading: '7. AI Agents and Automated Actions',
      body: (
        <>
          <p>Nula may allow you to activate AI agents or automated workflows that create, update, send, publish, assign, or otherwise act on information through Nula or connected services.</p>
          <p>Examples may include:</p>
          <ul>
            <li>Creating tasks in project-management systems.</li>
            <li>Sending messages or notifications.</li>
            <li>Updating CRM records.</li>
            <li>Generating documents or presentations.</li>
            <li>Assigning action items.</li>
            <li>Modifying information in connected applications.</li>
          </ul>
          <p>You are responsible for:</p>
          <ul>
            <li>Reviewing agent settings and permissions.</li>
            <li>Approving actions where approval is available.</li>
            <li>Monitoring actions performed on your behalf.</li>
            <li>Confirming the accuracy of instructions and outputs.</li>
            <li>Maintaining appropriate human oversight.</li>
            <li>Correcting or reversing unintended actions where possible.</li>
          </ul>
          <p>Nula is not responsible for losses caused by inaccurate instructions, insufficient oversight, third-party service behavior, or actions authorized through your account.</p>
          <p>You should not use automated agents for high-risk or irreversible decisions without meaningful human review.</p>
        </>
      ),
    },
    {
      heading: '8. Acceptable Use',
      body: (
        <>
          <p>You may not use Nula to:</p>
          <ul>
            <li>Violate any law or regulation.</li>
            <li>Infringe intellectual-property, privacy, publicity, or contractual rights.</li>
            <li>Record or monitor individuals without required authorization.</li>
            <li>Upload content you do not have permission to use.</li>
            <li>Generate or distribute malware or harmful code.</li>
            <li>Access another person&rsquo;s account or data without authorization.</li>
            <li>Interfere with or disrupt the Service.</li>
            <li>Bypass security, rate limits, or access controls.</li>
            <li>Reverse engineer, decompile, or attempt to extract Nula&rsquo;s source code or models, except where prohibited by law.</li>
            <li>Scrape or access the Service through unauthorized automated means.</li>
            <li>Use the Service to develop or train a competing AI model without our written permission.</li>
            <li>Misrepresent AI-generated content as verified or human-created where doing so would be deceptive.</li>
            <li>Use Nula for unlawful surveillance, discrimination, harassment, fraud, or impersonation.</li>
            <li>Place unreasonable or disproportionate demands on our infrastructure.</li>
          </ul>
          <p>We may investigate suspected violations and suspend or terminate access where necessary.</p>
        </>
      ),
    },
    {
      heading: '9. Sensitive and Regulated Information',
      body: (
        <>
          <p>Unless Nula has entered into a specific written agreement permitting such use, you may not use the Service to process information subject to specialized legal or regulatory requirements, including:</p>
          <ul>
            <li>Protected health information regulated by HIPAA.</li>
            <li>Payment-card information subject to PCI DSS.</li>
            <li>Classified government information.</li>
            <li>Highly sensitive biometric information.</li>
            <li>Information requiring specialized data residency or security controls.</li>
          </ul>
          <p>Payment for a subscription does not create a Business Associate Agreement or other specialized compliance agreement.</p>
          <p>You are responsible for determining whether the Service is appropriate for your legal and regulatory obligations.</p>
        </>
      ),
    },
    {
      heading: '10. Third-Party Services and Integrations',
      body: (
        <>
          <p>Nula may integrate with third-party products, such as:</p>
          <ul>
            <li>Calendar and communication platforms.</li>
            <li>Document-management and productivity tools.</li>
            <li>Project-management and CRM systems.</li>
            <li>Collaboration platforms.</li>
          </ul>
          <p>Your use of a third-party service is governed by that provider&rsquo;s terms and privacy policies.</p>
          <p>When you connect an integration, you authorize Nula to access and process information from that service according to the permissions you grant.</p>
          <p>You are responsible for:</p>
          <ul>
            <li>Maintaining valid third-party accounts.</li>
            <li>Configuring permissions appropriately.</li>
            <li>Ensuring you have authority to connect the account.</li>
            <li>Reviewing actions taken through the integration.</li>
          </ul>
          <p>Nula does not control and is not responsible for third-party services, their availability, their security, or changes they make to their products or APIs.</p>
          <p><strong>Google Workspace APIs &mdash; Limited Use.</strong> Nula&rsquo;s use and transfer of information received from Google APIs to any other app will adhere to the <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">Google API Services User Data Policy</a>, including the Limited Use requirements.</p>
          <p>Nula does not use raw or derived user data obtained through Google Workspace APIs to develop, improve, or train generalized or non-personalized AI and/or machine-learning models.</p>
        </>
      ),
    },
    {
      heading: '11. Nula Intellectual Property',
      body: (
        <>
          <p>Nula and its licensors own the Service, including its:</p>
          <ul>
            <li>Software.</li>
            <li>Technology.</li>
            <li>Models and systems.</li>
            <li>Interface and design.</li>
            <li>Branding and trademarks.</li>
            <li>Templates and documentation.</li>
            <li>Features and functionality.</li>
          </ul>
          <p>Except for the limited right to use the Service under these Terms, no intellectual-property rights are transferred to you.</p>
          <p>You may not copy, sell, sublicense, distribute, modify, or create derivative works from the Service except as expressly permitted by Nula.</p>
        </>
      ),
    },
    {
      heading: '12. Feedback',
      body: (
        <>
          <p>If you provide suggestions, ideas, or feedback regarding Nula, you grant us a worldwide, perpetual, irrevocable, royalty-free right to use that feedback without restriction or compensation.</p>
          <p>This does not grant us ownership of your User Content.</p>
        </>
      ),
    },
    {
      heading: '13. Subscriptions and Payments',
      body: (
        <>
          <p>Some Nula features require a paid subscription.</p>
          <p>By purchasing a subscription, you authorize us or our payment provider to charge the applicable fees and taxes.</p>
          <p>Unless otherwise stated:</p>
          <ul>
            <li>Subscriptions renew automatically.</li>
            <li>Fees are billed in advance.</li>
            <li>You may cancel through your account settings.</li>
            <li>Cancellation applies at the end of the current billing period.</li>
            <li>Payments are non-refundable except where required by law.</li>
            <li>Usage-based charges may be billed separately.</li>
            <li>Failure to pay may result in suspension or termination.</li>
          </ul>
          <p>We may change pricing or subscription features upon reasonable notice. Pricing changes generally apply beginning with your next renewal period.</p>
        </>
      ),
    },
    {
      heading: '14. Free Trials and Beta Features',
      body: (
        <>
          <p>We may offer free trials, previews, experimental features, or beta services.</p>
          <p>Beta features may:</p>
          <ul>
            <li>Be incomplete or unstable.</li>
            <li>Contain errors.</li>
            <li>Change without notice.</li>
            <li>Be discontinued at any time.</li>
            <li>Have different security, availability, or support commitments.</li>
          </ul>
          <p>Beta features are provided as-is and should not be used for critical or production-dependent workflows unless expressly authorized by Nula.</p>
        </>
      ),
    },
    {
      heading: '15. Privacy',
      body: (
        <>
          <p>Our collection and use of personal information are described in the Nula Privacy Policy.</p>
          <p>By using the Service, you acknowledge that your information will be processed as described in that policy.</p>
        </>
      ),
    },
    {
      heading: '16. Service Availability and Changes',
      body: (
        <>
          <p>We work to keep Nula available and reliable, but we do not guarantee uninterrupted, secure, or error-free operation.</p>
          <p>We may:</p>
          <ul>
            <li>Modify or discontinue features.</li>
            <li>Introduce or remove usage limits.</li>
            <li>Update models or third-party providers.</li>
            <li>Perform maintenance.</li>
            <li>Suspend portions of the Service for security or operational reasons.</li>
          </ul>
          <p>We will provide reasonable notice of material changes when practicable.</p>
        </>
      ),
    },
    {
      heading: '17. Termination',
      body: (
        <>
          <p>You may stop using Nula or cancel your account at any time.</p>
          <p>We may suspend or terminate your access if:</p>
          <ul>
            <li>You violate these Terms.</li>
            <li>Your use creates security or legal risk.</li>
            <li>Your account remains unpaid.</li>
            <li>Your activity materially disrupts the Service.</li>
            <li>We are required to do so by law.</li>
            <li>We discontinue the Service.</li>
          </ul>
          <p>Where reasonably possible, we will provide notice and an opportunity to correct the issue.</p>
          <p>Following termination, your right to use the Service ends. Certain provisions&mdash;including ownership, payment obligations, disclaimers, indemnification, liability limitations, and dispute terms&mdash;will survive.</p>
          <p>Data deletion and retention following termination are governed by our Privacy Policy and any applicable written agreement.</p>
        </>
      ),
    },
    {
      heading: '18. Disclaimers',
      body: (
        <>
          <p>THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE.&rdquo;</p>
          <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, NULA DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, NON-INFRINGEMENT, ACCURACY, AVAILABILITY, AND SECURITY.</p>
          <p>WE DO NOT WARRANT THAT:</p>
          <ul>
            <li>THE SERVICE WILL ALWAYS BE AVAILABLE OR ERROR-FREE.</li>
            <li>AI OUTPUTS WILL BE ACCURATE OR COMPLETE.</li>
            <li>AUTOMATED ACTIONS WILL PRODUCE AN INTENDED RESULT.</li>
            <li>THIRD-PARTY INTEGRATIONS WILL REMAIN AVAILABLE.</li>
            <li>CONTENT WILL NOT BE LOST OR CORRUPTED.</li>
            <li>THE SERVICE WILL MEET EVERY LEGAL OR REGULATORY REQUIREMENT APPLICABLE TO YOU.</li>
          </ul>
          <p>You are responsible for maintaining appropriate backups of important information.</p>
        </>
      ),
    },
    {
      heading: '19. Limitation of Liability',
      body: (
        <>
          <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, NULA AND ITS AFFILIATES, OFFICERS, EMPLOYEES, AGENTS, AND LICENSORS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES.</p>
          <p>THIS INCLUDES LOSS OF PROFITS, REVENUE, DATA, BUSINESS OPPORTUNITIES, GOODWILL, OR EXPECTED SAVINGS, AND LOSSES ARISING FROM AI-GENERATED OUTPUTS, MEETING RECORDINGS, AUTOMATED ACTIONS, OR THIRD-PARTY INTEGRATIONS.</p>
          <p>NULA&rsquo;S TOTAL LIABILITY ARISING OUT OF OR RELATING TO THE SERVICE WILL NOT EXCEED THE GREATER OF:</p>
          <ul>
            <li>THE AMOUNT YOU PAID TO NULA DURING THE 12 MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM; OR</li>
            <li>US&nbsp;$100.</li>
          </ul>
          <p>Some jurisdictions do not permit certain liability exclusions, so portions of this section may not apply to you.</p>
        </>
      ),
    },
    {
      heading: '20. Indemnification',
      body: (
        <>
          <p>To the extent permitted by law, you agree to defend, indemnify, and hold harmless Nula and its affiliates, officers, employees, agents, and licensors from claims, damages, losses, liabilities, and expenses arising from:</p>
          <ul>
            <li>Your use of the Service.</li>
            <li>Your User Content.</li>
            <li>Your violation of these Terms.</li>
            <li>Your violation of another person&rsquo;s rights.</li>
            <li>Your failure to obtain required recording or data-processing consent.</li>
            <li>Actions performed through your account, integrations, or AI agents.</li>
          </ul>
        </>
      ),
    },
    {
      heading: '21. Governing Law and Disputes',
      body: (
        <>
          <p>These Terms are governed by the laws of the State of Delaware, without regard to conflict-of-law principles.</p>
          <p>Any dispute arising from these Terms or the Service will be resolved exclusively in the state or federal courts located in the United States, Delaware, and each party consents to the jurisdiction of those courts.</p>
          <p>Nothing in these Terms prevents either party from seeking injunctive or equitable relief for unauthorized use, security incidents, or intellectual-property violations.</p>
        </>
      ),
    },
    {
      heading: '22. Changes to These Terms',
      body: (
        <>
          <p>We may update these Terms from time to time.</p>
          <p>If we make material changes, we may notify you through the Service, by email, or by posting an updated effective date.</p>
          <p>Your continued use of Nula after the updated Terms take effect constitutes acceptance of the revised Terms.</p>
        </>
      ),
    },
    {
      heading: '23. General Terms',
      body: (
        <>
          <p>These Terms, together with the Privacy Policy and any applicable order form or written agreement, constitute the entire agreement between you and Nula regarding the Service.</p>
          <p>If a provision is found unenforceable, the remaining provisions will remain effective.</p>
          <p>Our failure to enforce a provision is not a waiver of that provision.</p>
          <p>You may not assign these Terms without our written consent. We may assign them as part of a merger, acquisition, corporate reorganization, or sale of assets.</p>
          <p>Neither party is responsible for delays caused by events outside its reasonable control.</p>
        </>
      ),
    },
    {
      heading: '24. Contact',
      body: (
        <>
          <p>Questions regarding these Terms may be sent to:</p>
          <p>Nula Labs, Inc.<br />Email: {legalEmail}<br />Address: 2261 Market Street, STE 12762, San Francisco, CA 94114<br />Website: <a href="https://nulanotes.com">https://nulanotes.com</a></p>
        </>
      ),
    },
  ],
};

export const PRIVACY: LegalDocContent = {
  title: 'Privacy Policy',
  url: 'https://nulanotes.com/privacy',
  updated: 'Effective July 1, 2026',
  intro: (
    <>
      <p>At Nula, we believe your conversations belong to you.</p>
      <p>Our mission is to help teams transform conversations into visual strategies, decisions, and execution&mdash;not to monetize your data. We are committed to protecting your privacy and being transparent about how your information is collected, used, and secured.</p>
    </>
  ),
  sections: [
    {
      heading: 'Information We Collect',
      body: (
        <>
          <p>To provide the Nula experience, we may collect:</p>
          <ul>
            <li>Account information (name, email address, organization)</li>
            <li>Meeting recordings, transcripts, notes, and summaries</li>
            <li>Visual artifacts generated from your conversations (mind maps, diagrams, charts, timelines, workflows, etc.)</li>
            <li>Tasks, decisions, and initiatives you create</li>
            <li>Documents or files you upload</li>
            <li>Connected workspace data from services you authorize (such as Slack, Google Drive, Notion, Jira, Linear, HubSpot, Salesforce, and similar platforms)</li>
            <li>Basic device, browser, and usage information to operate and improve the service</li>
          </ul>
          <p>We only collect the information necessary to provide Nula&rsquo;s features.</p>
        </>
      ),
    },
    {
      heading: 'How We Use Your Information',
      body: (
        <>
          <p>We use your data to:</p>
          <ul>
            <li>Generate meeting notes, summaries, and action items</li>
            <li>Create visual representations of your work</li>
            <li>Organize conversations into Initiatives</li>
            <li>Power AI agents that help execute your workflows</li>
            <li>Synchronize information with connected applications you authorize</li>
            <li>Improve product performance, reliability, and security</li>
            <li>Provide customer support</li>
          </ul>
          <p><strong>We never sell your personal information.</strong></p>
        </>
      ),
    },
    {
      heading: 'AI & Model Training',
      body: (
        <>
          <p><strong>Your data belongs to you.</strong></p>
          <ul>
            <li>We do not allow third-party AI providers to use your content to train their models.</li>
            <li>By default, your customer content is not used to train Nula&rsquo;s AI models.</li>
            <li>If we ever introduce optional product-improvement programs using anonymized or aggregated information, participation will always be voluntary.</li>
          </ul>
        </>
      ),
    },
    {
      heading: 'Data Sharing',
      body: (
        <>
          <p>We only share information when necessary to operate the service.</p>
          <p>This may include trusted infrastructure and AI providers that process data on our behalf under strict confidentiality and security obligations.</p>
          <p><strong>We never sell or rent your information to advertisers or data brokers.</strong></p>
        </>
      ),
    },
    {
      heading: 'Integrations',
      body: (
        <>
          <p>If you connect third-party services such as Slack, Google Workspace, Microsoft 365, Notion, Linear, Jira, Salesforce, HubSpot, or similar platforms, Nula only accesses the information required to provide the features you enable.</p>
          <p>You may disconnect integrations at any time.</p>
        </>
      ),
    },
    {
      heading: 'Security',
      body: (
        <>
          <p>Protecting your data is one of our highest priorities.</p>
          <p>We use industry-standard security measures including:</p>
          <ul>
            <li>Encryption in transit (TLS)</li>
            <li>Encryption at rest</li>
            <li>Secure cloud infrastructure</li>
            <li>Access controls and authentication</li>
            <li>Continuous monitoring and security updates</li>
          </ul>
        </>
      ),
    },
    {
      heading: 'Data Retention',
      body: (
        <>
          <p><strong>You control your data.</strong></p>
          <p>You may edit or delete conversations, transcripts, recordings, visual artifacts, and initiatives at any time.</p>
          <p>We retain data only as long as necessary to provide the service or comply with legal obligations.</p>
        </>
      ),
    },
    {
      heading: 'Your Rights',
      body: (
        <>
          <p>Depending on your location, you may have the right to:</p>
          <ul>
            <li>Access your data</li>
            <li>Correct inaccurate information</li>
            <li>Export your data</li>
            <li>Delete your account</li>
            <li>Restrict or object to certain processing</li>
            <li>Withdraw consent where applicable</li>
          </ul>
          <p>To exercise these rights, contact us at {legalEmail}.</p>
        </>
      ),
    },
    {
      heading: 'Cookies',
      body: (
        <>
          <p>Our website uses cookies and similar technologies to:</p>
          <ul>
            <li>Keep you signed in</li>
            <li>Improve website performance</li>
            <li>Understand product usage</li>
            <li>Remember your preferences</li>
          </ul>
          <p>You can manage cookie preferences through your browser settings.</p>
        </>
      ),
    },
    {
      heading: 'Children’s Privacy',
      body: <p>Nula is intended for users who are at least 13 years old (or the minimum age required in your jurisdiction). We do not knowingly collect personal information from children.</p>,
    },
    {
      heading: 'International Users',
      body: <p>Your information may be processed in countries where Nula or its service providers operate. We implement appropriate safeguards to protect your data in accordance with applicable privacy laws.</p>,
    },
    {
      heading: 'Changes to This Policy',
      body: (
        <>
          <p>We may update this Privacy Policy from time to time.</p>
          <p>If we make material changes, we will notify you through the application, our website, or by email.</p>
        </>
      ),
    },
    {
      heading: 'Contact',
      body: (
        <>
          <p>If you have questions about privacy or security, please contact us:</p>
          <p>{legalEmail}</p>
        </>
      ),
    },
  ],
};
